/* eslint-disable @typescript-eslint/no-explicit-any */
import { setIn } from '@cloudio-saas/mutx';
import { areEqualShallow } from '@cloudio-saas/statex';
import {
  ItemIdProp,
  commonProps,
  RequiredItem,
  PropertyContext,
} from 'componentregistry';
import type {
  AnyManager,
  Item,
  Page,
  PropertyDefinition,
  PropertyType,
} from 'componentregistry';
import type { Platform } from 'componentregistry/platform/Platform';
import { itemWithDefaultValues } from 'registry/Components';

export function getDefaultValues<T>(mgr: AnyManager, property: string) {
  return mgr.defaultValue[property] as T;
}

export function excludeDefaultValues(
  mgr: AnyManager,
  props: Record<string, unknown>,
): Record<string, unknown> {
  const p = { ...props };
  const keys = Object.keys(mgr.defaultValue);
  keys.forEach((key) => {
    if (areEqualShallow(p[key], mgr.defaultValue[key])) {
      delete p[key];
    }
  });
  return p;
}

export function getPropertyDef(
  mgr: AnyManager,
  property: string,
): PropertyDefinition {
  if (property === 'itemId') {
    return ItemIdProp.itemId;
  }
  if (property in commonProps) {
    // @ts-ignore dynamic property
    return commonProps[property];
  }
  // @ts-ignore dynamic item
  return mgr.props[property];
}

export function onIDUpdate(
  platform: Platform,
  appUid: string,
  pageId: string,
  oldItemId: string,
  newItemId: string,
  item: Item<any>,
  mgr: AnyManager,
): Item<any> {
  if (!item?.props) {
    console.warn(`Missing props for item with id: ${item.itemId}`, item);
    return item;
  }
  const keys = Object.keys(mgr.props);
  keys.forEach((prop) => {
    // @ts-ignore mgr.props
    const def = mgr.props[prop];
    const val = item.props[prop];
    if (def.type === 'ItemID') {
      if (val === oldItemId) {
        // console.info(
        //   `Updating ${item.itemId}.${prop} from ${oldItemId} to ${newItemId}`,
        // );
        item = { ...item, props: { ...item.props } };
        item.props[prop] = newItemId;
        platform.showWarning(
          `Updated '${mgr.name}' - '${def.label}' from ${oldItemId} to ${newItemId}`,
          { appUid, pageId, itemId: item.itemId },
        );
      }
      if (
        platform.getRuntimeProp<any>(appUid, pageId, item.itemId, prop) ===
        oldItemId
      ) {
        platform.setRuntimeProp<any>(
          appUid,
          pageId,
          item.itemId,
          prop,
          (v: unknown) => {
            if (v === oldItemId) {
              // console.info(
              //   `Updating runtime ${item.itemId}.${prop} from ${oldItemId} to ${newItemId}`,
              // );
              return newItemId;
            }
            return v;
          },
        );
      }
    }
  });
  return item;
}

export function replaceItemPropertyValue({
  platform,
  appUid,
  pageId,
  propertyType,
  oldValue,
  newValue,
  item,
  mgr,
}: {
  platform: Platform;
  appUid: string;
  pageId: string;
  page: Page;
  propertyType: PropertyType;
  oldValue: unknown;
  newValue: unknown;
  item: Item<any>;
  mgr: AnyManager;
}): Item<any> {
  const keys: string[] = Object.keys(mgr.props);
  keys.forEach((prop) => {
    // @ts-ignore mgr.props
    const def = mgr.props[prop];
    const val = item.props[prop];
    if (def.type === propertyType) {
      if (val === oldValue) {
        // console.info(
        //   `Updating ${item.itemId}.${prop} from ${oldValue} to ${newValue}`,
        // );
        item = setIn(item, ['props', prop as string], newValue);
      }
      if (
        platform.getRuntimeProp<any>(appUid, pageId, item.itemId, prop) ===
        oldValue
      ) {
        platform.setRuntimeProp<any>(
          appUid,
          pageId,
          item.itemId,
          prop,
          (v: unknown) => {
            if (v === oldValue) {
              // console.info(
              //   `Updating runtime ${item.itemId}.${prop} from ${oldValue} to ${newValue}`,
              // );
              return newValue;
            }
            return v;
          },
        );
      }
    }
  });
  return item;
}

export function isRequired({
  platform,
  appUid,
  pageId,
  itemId,
  required,
  mgr,
}: {
  platform: Platform;
  appUid: string;
  pageId: string;
  itemId: string;
  required:
    | ((item: RequiredItem<never>, context: PropertyContext) => boolean)
    | boolean
    | undefined;
  mgr: AnyManager;
}) {
  if (typeof required === 'function') {
    const item = platform.getItem(appUid, pageId, itemId) as Item<any>;
    if (!item) {
      platform.showError(`isRequired: Missing item with id ${itemId}!`);
      return false;
    }
    const itemWithDefaults = itemWithDefaultValues(item);
    return required(itemWithDefaults, {
      appUid,
      pageId,
      itemId,
      platform,
      mgr,
    });
  }
  return !!required;
}
